import React, {FC, useEffect, useState} from "react";
import {Result, Space, Spin, Typography} from "antd";
import {useTranslation} from "react-i18next";
import {useParams, useSearchParams} from "react-router-dom";
import "../Styles/PublicRegistrationPage.css";
import {PublicShell} from "../Components/ui/PublicShell";

type Invitation = {
    eventName: string;
    eventNameHr?: string;
    eventNameEn?: string;
    eventStartDate?: string;
    eventEndDate?: string;
    eventPlace?: string;
    eventAddress?: string;
    onSiteRegistrationInstructions?: string;
    onSiteRegistrationInstructionsHr?: string;
    onSiteRegistrationInstructionsEn?: string;
    participantFirstName: string;
    participantLastName: string;
    participantCompany?: string;
    registrationStatus: number | string;
};

const formatInvitationDate = (value: string | undefined, language: string): string => {
    const datePart = value?.slice(0, 10);
    if (!datePart || !/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return "";

    const date = new Date(`${datePart}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return "";

    return new Intl.DateTimeFormat(language === "hr" ? "hr-HR" : "en-GB", {
        day: "numeric", month: "short", year: "numeric", timeZone: "UTC"
    }).format(date);
};

type InvitationState =
    | {kind: "loading"}
    | {kind: "valid"; invitation: Invitation}
    | {kind: "invalid"}
    | {kind: "unavailable"};

const PublicInvitationPage: FC = () => {
    const {t, i18n} = useTranslation();
    const {eventIdentifier = ""} = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token")?.trim() ?? "";
    // Opened from a confirmed email-confirmation link instead of the QR invitation.
    const registrationId = Number(searchParams.get("registrationId"));
    const confirmationToken = searchParams.get("confirmationToken")?.trim() ?? "";
    const fromConfirmation = !token && Number.isInteger(registrationId) && registrationId > 0 && !!confirmationToken;
    const [state, setState] = useState<InvitationState>({kind: "loading"});

    useEffect(() => {
        if (!eventIdentifier || (!token && !fromConfirmation)) {
            setState({kind: "invalid"});
            return;
        }

        const abortController = new AbortController();
        setState({kind: "loading"});
        const eventPath = `/api/public/events/${encodeURIComponent(eventIdentifier)}`;
        const detailsUrl = fromConfirmation
            ? `${eventPath}/registrations/${registrationId}/details?token=${encodeURIComponent(confirmationToken)}`
            : `${eventPath}/registration-codes/validate?token=${encodeURIComponent(token)}`;

        void fetch(
            detailsUrl,
            {headers: {Accept: "application/json"}, signal: abortController.signal}
        ).then(async response => {
            if (!response.ok) {
                setState({kind: response.status >= 500 ? "unavailable" : "invalid"});
                return;
            }

            setState({kind: "valid", invitation: await response.json() as Invitation});
        }).catch(error => {
            if (!(error instanceof DOMException && error.name === "AbortError")) {
                setState({kind: "unavailable"});
            }
        });

        return () => abortController.abort();
    }, [eventIdentifier, token, fromConfirmation, registrationId, confirmationToken]);

    const statusLabels: Record<string, string> = {
        "0": t("public-invitation-status-submitted", {defaultValue: "Submitted"}),
        "1": t("public-invitation-status-rejected", {defaultValue: "Rejected"}),
        "2": t("public-invitation-status-invited", {defaultValue: "Invited"}),
        "3": t("public-invitation-status-registered", {defaultValue: "Registered"}),
        "4": t("public-invitation-status-confirmed", {defaultValue: "Confirmed"})
    };

    const invitation = state.kind === "valid" ? state.invitation : null;
    const language = i18n.language?.toLowerCase().startsWith("hr") ? "hr" : "en";
    const eventName = (language === "hr" ? invitation?.eventNameHr : invitation?.eventNameEn)?.trim()
        || invitation?.eventName;
    const instructions = (language === "hr"
        ? invitation?.onSiteRegistrationInstructionsHr
        : invitation?.onSiteRegistrationInstructionsEn)?.trim()
        || invitation?.onSiteRegistrationInstructions?.trim();
    const startDate = formatInvitationDate(invitation?.eventStartDate, language);
    const endDate = formatInvitationDate(invitation?.eventEndDate, language);
    const dateRange = [startDate, endDate && endDate !== startDate ? endDate : ""].filter(Boolean).join(" – ");
    const location = [invitation?.eventPlace?.trim(), invitation?.eventAddress?.trim()].filter(Boolean).join(", ");

    const result = invitation ? (
        <Result
            status="info"
            title={fromConfirmation
                ? t("public-registration-details-title", {defaultValue: "Registration details"})
                : t("public-invitation-title", {defaultValue: "Event invitation"})}
            subTitle={
                <Space direction="vertical" size={12}>
                    <Typography.Title level={3} style={{margin: 0}}>{eventName}</Typography.Title>
                    <Typography.Text>
                        {t("public-invitation-participant", {defaultValue: "Participant"})}: {[invitation.participantFirstName, invitation.participantLastName].filter(Boolean).join(" ")}
                    </Typography.Text>
                    {invitation.participantCompany?.trim() && (
                        <Typography.Text>{t("public-invitation-company", {defaultValue: "Company"})}: {invitation.participantCompany.trim()}</Typography.Text>
                    )}
                    {dateRange && (
                        <Typography.Text>{t("public-invitation-dates", {defaultValue: "Dates"})}: {dateRange}</Typography.Text>
                    )}
                    {location && (
                        <Typography.Text>{t("public-invitation-location", {defaultValue: "Location"})}: {location}</Typography.Text>
                    )}
                    <Typography.Text>
                        {t("public-invitation-status", {defaultValue: "Status"})}: {statusLabels[String(invitation.registrationStatus)] ?? String(invitation.registrationStatus)}
                    </Typography.Text>
                    {instructions && (
                        <Typography.Text>{t("public-invitation-on-site-instructions", {defaultValue: "On-site instructions"})}: {instructions}</Typography.Text>
                    )}
                    <Typography.Text>
                        {fromConfirmation
                            ? t("public-registration-details-help", {defaultValue: "Your QR invitation arrives by email once the organizer invites you; show it at the registration desk. Opening this page does not change your registration status."})
                            : t("public-invitation-help", {defaultValue: "Show the QR code from your email at the registration desk. Opening this page does not change your registration status."})}
                    </Typography.Text>
                </Space>
            }
        />
    ) : state.kind === "loading" ? (
        <Result
            status="info"
            icon={<Spin/>}
            title={t("public-invitation-loading", {defaultValue: "Checking your invitation"})}
        />
    ) : (
        <Result
            status="error"
            title={state.kind === "invalid"
                ? t("public-invitation-invalid", {defaultValue: "This invitation link is invalid or no longer active."})
                : t("public-invitation-unavailable", {defaultValue: "We could not check your invitation right now."})}
            subTitle={t("public-invitation-error-help", {defaultValue: "Check that you opened the complete link from your email, or contact the event organizer."})}
        />
    );

    return (
        <PublicShell live>
            {result}
        </PublicShell>
    );
};

export default PublicInvitationPage;
